import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

import styles from '../../styles/Dashboard.module.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function toLabel(isoDate) {
  const d = new Date(isoDate);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}`;
}

function toRangeLabel(points) {
  if (!points?.length) return '';
  return `${toLabel(points[0].date)} - ${toLabel(
    points[points.length - 1].date
  )}`;
}

const baseDataset = {
  borderRadius: 12,
  borderSkipped: false,
  barPercentage: 0.8,
  categoryPercentage: 0.9, 
};

const INCOME_COLOR = '#7FCF9B';
const EXPENSE_COLOR = '#F08A8A';

const drawEmptyDotsPlugin = {
  id: 'drawEmptyDots',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, i) => {
      const meta = chart.getDatasetMeta(i);
      meta.data.forEach((element, index) => {
        const rawVal = dataset.raw[index];
        if (rawVal === 0 && element.x && element.base && element.width) {
          ctx.save();
          ctx.beginPath();
          ctx.fillStyle = '#222222';

          const radius = (element.width * 0.95) / 2;

          const yPos = element.base - radius;
          ctx.arc(element.x, yPos, radius, 0, 2 * Math.PI);
          ctx.fill();
          ctx.restore();
        }
      });
    });
  }
};

const StatsChart = ({ month, onRangeChange }) => {
  const [chartData, setChartData] = useState(null);
  const [animate, setAnimate] = useState(false);
  const [status, setStatus] = useState('idle');

  const [isMobile, setIsMobile] = useState(() => 
    typeof window !== 'undefined' && window.innerWidth <= 600
  );

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!month) return;

    let cancelled = false;

    (async () => {
      try {
        setStatus('loading');
        setAnimate(false);
        setChartData(null);

        const res = await fetch(`/api/stats/monthDays?month=${month}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Ошибка загрузки графика');

        const points = Array.isArray(data.points) ? data.points : [];
        if (!points.length) {
          if (!cancelled) {
            setStatus('empty');
            setAnimate(false);
            setChartData(null);
            onRangeChange?.('');
          }
          return;
        }

        const labels = points.map((p) => toLabel(p.date));
        onRangeChange?.(toRangeLabel(points));

        const incomeRaw = points.map((p) => Number(p?.income || 0));
        const expenseRaw = points.map((p) => Number(p?.expense || 0));

        const hasAnyTransactions =
          incomeRaw.some((v) => v > 0) || expenseRaw.some((v) => v > 0);

        if (!hasAnyTransactions) {
          if (!cancelled) {
            setStatus('noTx');
            setAnimate(false);
            setChartData(null);
          }
          return;
        }

        const incomeBg = incomeRaw.map((v) =>
          v === 0 ? 'transparent' : INCOME_COLOR
        );
        const expenseBg = expenseRaw.map((v) =>
          v === 0 ? 'transparent' : EXPENSE_COLOR
        );

        if (!cancelled) {
          setStatus('ok');
          setChartData({
            labels,
            datasets: [
              {
                ...baseDataset,
                label: 'Доход',
                data: new Array(labels.length).fill(0),
                backgroundColor: incomeBg,
                raw: incomeRaw,
              },
              {
                ...baseDataset,
                label: 'Расход',
                data: new Array(labels.length).fill(0),
                backgroundColor: expenseBg,
                raw: expenseRaw,
              },
            ],
          });
        }

        requestAnimationFrame(() => {
          if (cancelled) return;

          setAnimate(true);
          setChartData({
            labels,
            datasets: [
              {
                ...baseDataset,
                label: 'Доход',
                data: incomeRaw,
                backgroundColor: incomeBg,
                raw: incomeRaw,
              },
              {
                ...baseDataset,
                label: 'Расход',
                data: expenseRaw,
                backgroundColor: expenseBg,
                raw: expenseRaw,
              },
            ],
          });
        });
      } catch {
        if (!cancelled) {
          setStatus('error');
          setChartData(null);
          setAnimate(false);
          onRangeChange?.('');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [month, onRangeChange]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: animate
        ? { duration: 800, easing: 'easeOutCubic' }
        : { duration: 0 },
      
      interaction: {
        mode: 'index',
        intersect: false, 
      },

      plugins: {
        legend: { display: false },
        title: { display: false },
        tooltip: {
          enabled: true,
          padding: 12,
          boxPadding: 6,
          usePointStyle: true,
          callbacks: {
            label: (ctx) => {
              const ds = ctx.dataset;
              const rawArr = ds?.raw || [];
              const rawVal = rawArr[ctx.dataIndex] ?? 0;
              return `${ds.label}: ${rawVal} ₽`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false, drawTicks: false },
          ticks: {
            color: '#cacaca',
            font: { size: 12, family: 'Mulish', weight: '400' },
            padding: 10,
            autoSkip: true,
            maxRotation: 0,
            minRotation: 0,
            maxTicksLimit: isMobile ? 6 : 14,
          },
          border: { display: false },
        },
        y: {
          grid: { display: false, drawTicks: false },
          ticks: { display: false },
          border: { display: false },
        },
      },
    }),
    [animate, isMobile]
  );

  return (
    <div
      style={{
        height: '390px',
        width: '100%',
        marginTop: '30px',
        position: 'relative',
      }}>
      {status === 'loading' ? (
        <div className={styles.chart__loading}>Загрузка графика…</div>
      ) : null}

      {status === 'empty' ? (
        <div className={styles.chart__empty}>
          Нет данных за период
          <div className={styles.chart__emptyText}>
            Добавь первую операцию — здесь появится аналитика
          </div>
        </div>
      ) : null}

      {status === 'noTx' ? (
        <div className={styles.chart__empty}>
          Нет транзакций за последние 7 дней
          <div className={styles.chart__emptyText}>
            Добавь операцию — график начнёт заполняться
          </div>
        </div>
      ) : null}

      {status === 'error' ? (
        <div className={styles.chart__error}>Не удалось загрузить график</div>
      ) : null}

      {chartData ? (
        <Bar data={chartData} options={options} plugins={[drawEmptyDotsPlugin]} />
      ) : null}
    </div>
  );
};

export default StatsChart;